import { useCallback, useEffect, useState } from "react";
import { listarPuestos, crearPuesto, actualizarPuesto, eliminarPuesto } from "../../../../services/puestosService";
import { showToast } from "../../../../utils/showToast";

const initialForm = { nombre: "", activo: true };

export default function usePuestosLogic() {
  const [puestos, setPuestos]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [modal, setModal]           = useState("");
  const [saving, setSaving]         = useState(false);
  const [formNuevo, setFormNuevo]   = useState(initialForm);
  const [formEditar, setFormEditar] = useState(initialForm);
  const [editando, setEditando]     = useState(null);
  const [deleting, setDeleting]     = useState(false);

  const fetchPuestos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarPuestos();
      setPuestos(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      const msg = err.message || "No se pudo obtener los puestos.";
      setError(msg);
      showToast({ type: "danger", message: msg });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPuestos(); }, [fetchPuestos]);

  const abrirCrear = useCallback(() => {
    setFormNuevo(initialForm);
    setModal("crear");
  }, []);

  const abrirEditar = useCallback((puesto) => {
    setEditando(puesto);
    setFormEditar({ nombre: puesto.nombre, activo: !!puesto.activo });
    setModal("editar");
  }, []);

  const abrirEliminar = useCallback((puesto) => {
    setEditando(puesto);
    setModal("eliminar");
  }, []);

  const cerrarModal = useCallback(() => {
    setModal("");
    setEditando(null);
  }, []);

  const handleCrear = useCallback(async () => {
    if (!formNuevo.nombre.trim()) { showToast({ type: "warning", message: "El nombre es requerido." }); return; }
    setSaving(true);
    try {
      const nuevo = await crearPuesto(formNuevo);
      setPuestos((prev) => [...prev, nuevo]);
      showToast({ type: "success", message: "Puesto creado correctamente." });
      cerrarModal();
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo crear el puesto." });
    } finally { setSaving(false); }
  }, [formNuevo, cerrarModal]);

  const handleActualizar = useCallback(async () => {
    if (!formEditar.nombre.trim()) { showToast({ type: "warning", message: "El nombre es requerido." }); return; }
    setSaving(true);
    try {
      const actualizado = await actualizarPuesto(editando.id, formEditar);
      setPuestos((prev) => prev.map((p) => p.id === actualizado.id ? actualizado : p));
      showToast({ type: "success", message: "Puesto actualizado correctamente." });
      cerrarModal();
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo actualizar el puesto." });
    } finally { setSaving(false); }
  }, [editando, formEditar, cerrarModal]);

  const handleEliminar = useCallback(async () => {
    if (!editando) return;
    setDeleting(true);
    try {
      await eliminarPuesto(editando.id);
      setPuestos((prev) => prev.filter((p) => p.id !== editando.id));
      showToast({ type: "success", message: "Puesto eliminado correctamente." });
      cerrarModal();
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo eliminar el puesto." });
    } finally { setDeleting(false); }
  }, [editando, cerrarModal]);

  return {
    puestos, loading, error, reload: fetchPuestos,
    modal, saving, editando, deleting,
    formNuevo, setFormNuevo,
    formEditar, setFormEditar,
    abrirCrear, abrirEditar, abrirEliminar, cerrarModal,
    handleCrear, handleActualizar, handleEliminar,
  };
}
